import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trackPurchase, readVisitorId } from '../lib/fbpixel';
import { trackRedditPurchase } from '../lib/redditPixel';
import { trackTradedoublerSale } from '../lib/tradedoubler';
import {
  phSignupOtpSent,
  aliasLandingId,
  phFailure,
  phOauthStarted,
} from '../lib/posthog';
import googleLogo from '../assets/google.svg';
import { trackPaymentCompleted } from '../lib/intercom';
import { captureAttributionFromUrl } from '../lib/attribution';
import { API_BASE } from '../lib/apiConfig';
import AuthStepper from '../components/AuthStepper';
import AuthHelp, { LastUsedTag } from '../components/AuthHelp';
import { getLastSignIn, markSignInStarted } from '../lib/lastSignIn';
import { linkPaidSubscription } from '../lib/freeAccount';
import '@styles/pages/signup.css';

/* ─────────────────────────────────────────────────────────────────
   PLAN SUMMARY (renders inside the Case-opened welcome state)
   Reads the `plan` query param passed by Stripe success_url and
   shows the customer what they bought right after payment.
   ───────────────────────────────────────────────────────────────── */
const PLAN_META: Record<
  string,
  { name: string; price: number; features: string }
> = {
  annual: {
    name: 'Annual Plan',
    price: 499,
    features: '6 bill negotiations · Unlimited EOB reviews · Family coverage',
  },
  one_off: {
    name: 'Pay Per Bill',
    price: 129,
    features: '1 bill negotiation · Refunded in full if we don\'t lower your bill',
  },
  eob: {
    name: 'EOB Review',
    price: 69,
    features: '1 EOB audit · 48-hour written report',
  },
  action_plan: {
    name: 'Personalized Action Plan',
    price: 49,
    features: 'Bill analyzed, errors flagged, step-by-step plan to lower it · Within 24 hours of your upload',
  },
};

function PlanSummary({ plan }: { plan: string | null }) {
  const meta = plan ? PLAN_META[plan] : null;
  if (!meta) return null;
  return (
    <div className="plan-summary">
      <div className="plan-summary-row">
        <span className="plan-summary-name">{meta.name}</span>
        <span className="plan-summary-paid">
          Paid · <span className="num">${meta.price}</span>
        </span>
      </div>
      <div className="plan-summary-features">{meta.features}</div>
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const checkoutSuccess = searchParams.get('checkout') === 'success';
  const checkoutRef = searchParams.get('ref');
  const checkoutPlan = searchParams.get('plan'); // 'one_off' | 'annual'
  /* Stripe checkout session id (cs_...). Added to the success URL by
     createCheckout.ts on 2026-06-01. Used as the Purchase event_id so
     the client-side pixel and the webhook's server-side CAPI dedupe
     against the same id, and so /api/conversion can verify with Stripe
     before forwarding to Meta. May be absent on legacy URLs from before
     the rollout — Purchase tracking simply no-ops in that case. */
  const checkoutSessionId = searchParams.get('session_id');
  const phDistinctId = searchParams.get('ph_distinct_id');
  const returningEmail = searchParams.get('email');
  const isReturning = searchParams.get('returning') === 'true';

  /* The plan button the visitor clicked on the marketing site, if any.
     Free signup doesn't need it, the pay popup preselects it later. */
  const wantedPlan = searchParams.get('want');
  useEffect(() => {
    if (wantedPlan === 'one_off' || wantedPlan === 'annual') {
      try {
        localStorage.setItem('wanted_plan', wantedPlan);
      } catch {
        /* the popup just defaults to Pay Per Bill */
      }
    }
  }, [wantedPlan]);
  // How they signed in last time on this device, for the "Last used" tag.
  const [lastUsed] = useState(getLastSignIn);

  const [email, setEmail] = useState(returningEmail ?? '');
  /* Free signups only. Goes to the server with the claim so the emails
     can say hello properly. */
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  /* AppGate sends people back here with ?error=google_unavailable when
     Supabase turns a new Google account away (public signups off). */
  const [error, setError] = useState<string | null>(
    searchParams.get('error') === 'google_unavailable'
      ? "Google sign up isn't available right now. Use your email below and we'll send you a code."
      : null,
  );
  /* `paymentVerified` flips true ONLY after /stripe/case-ref confirms a
     subscription row exists for this checkout_ref. That row is created by
     the Stripe webhook, which only fires after Stripe has confirmed
     payment_status === 'paid' — so a non-null short_id from case-ref is a
     reliable signal that the purchase is real. The Purchase pixel/CAPI
     fire is gated on this flag (see PURCHASE TRACKING useEffect below) so
     someone who just hits /signup?checkout=success&ref=anything does NOT
     manufacture a fake Purchase event in Meta. */
  const [paymentVerified, setPaymentVerified] = useState(false);

  /* Set when someone who is already signed in comes back from Stripe (a
     free account that just paid). They don't need this form, so we wait
     for the payment to land, link it and send them into the portal. */
  const [finishingPayment, setFinishingPayment] = useState(false);

  /* ===============================
     RESTORE checkout_ref + plan (CRITICAL)
     The ref is used by /verify and AppGate to link the orphan
     subscription. The plan is read by AuthSidecar on /verify and
     /onboarding to keep the case-file panel populated after the
     URL params drop. Both are cleared by AppGate post-link.
  =============================== */
  useEffect(() => {
    if (checkoutRef) {
      localStorage.setItem('checkout_ref', checkoutRef);
    }
    if (checkoutPlan) {
      localStorage.setItem('checkout_plan', checkoutPlan);
    }
  }, [checkoutRef, checkoutPlan]);

  /* ===============================
     FETCH REAL CASE FILE SHORT_ID
     The subscription row was created by the Stripe webhook with a
     DB-generated short_id (ML-XXXXX). Stash it in localStorage so
     AuthSidecar can show the real id instead of the truncated
     checkout_ref placeholder. Pre-auth, uses the public /case-ref
     endpoint scoped to unlinked subscriptions only.

     If the migration hasn't landed yet, the endpoint returns null
     and the sidecar falls back to the placeholder gracefully.
  =============================== */
  useEffect(() => {
    if (!checkoutRef) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/stripe/case-ref?checkout_ref=${encodeURIComponent(checkoutRef)}`,
        );
        if (!res.ok || cancelled) return;
        const { short_id } = (await res.json()) as { short_id: string | null };
        if (short_id && !cancelled) {
          localStorage.setItem('checkout_short_id', short_id);
          /* A short_id only exists because the Stripe webhook fired and
             created the unlinked subscription row — meaning Stripe has
             confirmed payment_status === 'paid'. This is our gate for
             firing client-side Purchase analytics below. */
          setPaymentVerified(true);
        }
      } catch {
        /* Silent, AuthSidecar handles the missing case gracefully. */
      }
    })();
    return () => { cancelled = true; };
  }, [checkoutRef]);

  /* ===============================
     POSTHOG: Alias landing page distinct ID
  =============================== */
  useEffect(() => {
    if (phDistinctId) {
      aliasLandingId(phDistinctId);
    }
  }, [phDistinctId]);

  /* ===============================
     ATTRIBUTION: First-touch capture
     Pulls utm_* / ml_vid / document.referrer off the current URL on mount
     and stashes in localStorage so Onboarding.tsx can write them to
     profiles at upsert time. Idempotent, first-touch is sticky.
  =============================== */
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);

  /* Upload first, account after (2026-10-16). New visitors start on /start
     with their bill, and the account comes after. This page stays for
     people back from Stripe, returning customers, and the Google error
     bounce. The query string goes along so attribution and ?want= survive.
     The effects above have already run by the time this fires. */
  useEffect(() => {
    if (checkoutSuccess || isReturning || searchParams.get('error')) return;
    navigate(`/start${window.location.search}`, { replace: true });
  }, [checkoutSuccess, isReturning, searchParams, navigate]);

  /* ===============================
     PURCHASE TRACKING: Verified-only fire
     Fires the client-side Meta + Reddit + PostHog + Intercom purchase
     events ONLY after /stripe/case-ref has confirmed the Stripe webhook
     created the subscription row (i.e. Stripe says payment_status ===
     'paid'). Previously this fired on URL params alone, which let
     anyone — Meta's link-verification crawler, an old success-URL link
     click, an internal QA refresh — manufacture a fake Purchase event
     and pollute the ad account's conversion data. See the comment on
     the `paymentVerified` state declaration above for the full rationale.
  =============================== */
  useEffect(() => {
    if (!paymentVerified) return;
    /* The Stripe session id IS the Purchase event_id everywhere else
       (webhook CAPI fire, /api/conversion server-side verify). If we
       can't read it from the URL — e.g. a user who started checkout
       before the createCheckout success-URL change — skip the
       client-side fire entirely. The webhook's server-side CAPI Purchase
       still goes through, so Meta is informed; we just lose a small
       amount of browser-fingerprint match quality on this one user. */
    if (!checkoutSessionId) return;

    /* 🧪 TEST-MODE GATE (2026-07-02)
       Stripe test checkouts produce cs_test_* session ids. The webhook has
       the mirror gate (session.livemode) for its server-side CAPI fires,
       but the webhook ALSO creates the subscription row for QA flows —
       which flips paymentVerified here and was firing browser-side Meta +
       Reddit + PostHog Purchases into production analytics during QA
       (phantom Purchases Jun 18/25/26, no matching live Stripe charge).
       QA still gets the full signup flow; only analytics fires are skipped. */
    if (checkoutSessionId.startsWith('cs_test_')) {
      console.log('🧪 Test-mode session id, skipping client-side purchase analytics');
      return;
    }

    const valueMap: Record<string, number> = { annual: 499, one_off: 129, eob: 69, action_plan: 49 };
    const value = valueMap[checkoutPlan ?? ''] ?? 129;
    const contentName = checkoutPlan ?? 'one_off';

    /* Advanced matching for the client-side leg (2026-07-27). This is the
       FIRST purchase — the conversion an acquisition campaign actually
       buys — and it was firing with no identity at all, so Meta and Reddit
       had only cookies to match on. `mediloop_vid` is scoped to
       `.mediloop.ai`, so it is readable here and is the same visitor id
       the marketing site saw at ad-click time; the webhook's server leg
       uses the identical fallback chain (user_id → visitor_id →
       checkout_ref), so both legs land on one identity. `returningEmail`
       is the ?email= param, present for returning customers only — the
       server leg carries the real Stripe email regardless. */
    const purchaseExternalId = readVisitorId() || checkoutRef || undefined;
    const purchaseEmail = returningEmail || undefined;

    trackPurchase({
      value,
      contentName,
      eventID: checkoutSessionId,
      email: purchaseEmail,
      externalId: purchaseExternalId,
    });

    // Reddit Purchase — same eventID as the webhook's Reddit CAPI fire
    // so Reddit dedupes the two. sessionStorage dedup inside
    // trackRedditPurchase covers refresh-within-session.
    trackRedditPurchase({
      value,
      contentName,
      eventID: checkoutSessionId,
      email: purchaseEmail,
      externalId: purchaseExternalId,
    });

    // TradeDoubler affiliate sale. The SDK reads the tduid cookie the
    // marketing site set on .mediloop.ai at click time, so nothing has to be
    // carried through checkout. Same session id as the other legs, dedup on
    // refresh lives inside trackTradedoublerSale. There's no voucher here
    // because Stripe applies promo codes on its side and the success URL
    // doesn't tell us which one was used.
    trackTradedoublerSale({
      value,
      transactionId: checkoutSessionId,
    });

    /* 🚫 2026-08-08 — the PostHog `purchase` fire moved SERVER-SIDE.
       apps/server stripe/webhook.ts::captureServerPurchase is now the single
       source. Two reasons:
         1. Adblockers and Safari ITP kill this call for a non-random slice of
            users, so PostHog revenue was biased low against exactly the
            privacy-conscious segment. The webhook always runs. That argument
            was already made in this repo for the Meta CAPI fire and simply
            never applied to PostHog.
         2. Two sources with no shared dedup is what produced 26 PostHog
            purchase events against 11 Stripe charges over 90 days.
       The webhook keys the event on session.metadata.posthog_distinct_id, the
       same marketing-side id this page bootstraps from, so the funnel still
       joins end to end. The Meta and Reddit legs above stay client-side —
       those platforms dedupe against the webhook by shared event_id. */

    /* Intercom's payment-completed had no dedup of any kind, so it
       double-counted on every refresh of this page. Guarded on the Stripe
       session id. (PostHog's `purchase` is no longer fired here at all — see
       the note below.) */
    const intercomGuard = `ic_payment_completed:${checkoutSessionId}`;
    let alreadySentToIntercom = false;
    try {
      alreadySentToIntercom = Boolean(localStorage.getItem(intercomGuard));
      if (!alreadySentToIntercom) localStorage.setItem(intercomGuard, '1');
    } catch {
      /* localStorage unavailable — send rather than lose the event */
    }
    if (!alreadySentToIntercom) {
      trackPaymentCompleted({ plan: contentName, amount: value });
    }

    /* ⚠️ 2026-08-08 — the root-cause fix for purchase double-counting.
       This page never stripped its query string (Plan.tsx has always done so
       via history.replaceState). Every refresh of the post-checkout URL
       re-ran /stripe/case-ref, re-flipped paymentVerified and re-fired this
       whole effect. Meta and Reddit were protected by sessionStorage guards
       inside trackPurchase / trackRedditPurchase; PostHog and Intercom were
       not. Measured over 90 days: 26 PostHog `purchase` events across 18
       people against 11 succeeded Stripe charges, with five people firing
       2-4x in one day (one logged a $69 EOB as $276 of $revenue).

       We strip ONLY `session_id`, not the whole query string: `checkout`,
       `ref` and `plan` still drive the "case opened" welcome UI, so blanking
       those would break the page on refresh. Dropping session_id is enough,
       because the effect early-returns without it. React Router's
       useSearchParams does not observe replaceState, so the values already
       held by this component stay intact for the rest of the render. */
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('session_id')) {
        url.searchParams.delete('session_id');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {
      /* non-fatal — the localStorage guards above still hold the line */
    }
  }, [paymentVerified, checkoutSessionId, checkoutPlan, checkoutRef, returningEmail]);

  /* ===============================
     ALREADY SIGNED IN + BACK FROM STRIPE (2026-10-05)
     A free account pays from inside the portal and Stripe returns it
     here like every other checkout. Wait for the webhook to write the
     subscription (case-ref returns the short_id once it has), let the
     purchase tracking above fire, then link the subscription and go to
     the portal. If the webhook is slow we still try to link and move on,
     AppGate links by email on a later visit anyway.
  =============================== */
  useEffect(() => {
    if (!checkoutSuccess || !checkoutRef) return;
    let cancelled = false;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    (async () => {
      const { data } = await supabase.auth.getSession();
      const session = data.session;
      if (cancelled || !session?.user) return;

      setFinishingPayment(true);

      for (let attempt = 0; attempt < 14 && !cancelled; attempt += 1) {
        try {
          const res = await fetch(
            `${API_BASE}/stripe/case-ref?checkout_ref=${encodeURIComponent(checkoutRef)}`,
          );
          const body = res.ok ? ((await res.json()) as { short_id: string | null }) : null;
          if (body?.short_id) {
            setPaymentVerified(true);
            // One beat so the purchase tracking effect runs before we leave.
            await wait(800);
            break;
          }
        } catch {
          /* try again */
        }
        await wait(1500);
      }
      if (cancelled) return;

      await linkPaidSubscription(session.user.id, session.access_token ?? null);
      if (cancelled) return;
      navigate('/', { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutSuccess, checkoutRef, navigate]);

  /* ===============================
     EMAIL OTP SIGNUP
  =============================== */
  const handleEmailSignup = async () => {
    if (!email) return;

    const normalizedEmail = email.trim().toLowerCase();

    setLoading(true);
    setError(null);

    /* Include checkout_ref + plan in the verify redirect so a customer
       opening the magic link on a DIFFERENT device (paid on laptop,
       opens email on phone) can re-hydrate the AuthSidecar's case file
       context. /verify reads these as URL params and refreshes
       localStorage on arrival. */
    const refParam = checkoutRef ? `&ref=${encodeURIComponent(checkoutRef)}` : '';
    const planParam = checkoutPlan ? `&plan=${encodeURIComponent(checkoutPlan)}` : '';
    const verifyUrl = `${
      window.location.origin
    }/verify?email=${encodeURIComponent(normalizedEmail)}${refParam}${planParam}`;

    /* Server-side gate first (2026-08-26). Public signups are switched off
       in Supabase, so the account has to be created by our server. It does
       that for a verified purchase (a checkout_ref row from the Stripe
       webhook, or the email being on a subscription) and, since
       2026-10-05, for a free account too. See apps/server
       routes/claimAccount.ts. */
    let storedRef: string | null = null;
    try {
      storedRef = localStorage.getItem('checkout_ref');
    } catch {
      /* localStorage unavailable — the URL ref still covers fresh checkouts */
    }
    const claimRef = checkoutRef ?? storedRef ?? undefined;
    try {
      const claimRes = await fetch(`${API_BASE}/auth/claim-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: normalizedEmail,
          checkout_ref: claimRef,
          first_name: firstName.trim() || undefined,
        }),
      });
      if (!claimRes.ok) {
        const body = (await claimRes.json().catch(() => ({}))) as { error?: string };
        phFailure({
          step: 'signup_claim',
          message: body.error ?? `http_${claimRes.status}`,
          code: claimRes.status,
        });
        setLoading(false);
        setError(body.error ?? 'We could not create your account. Try again in a moment.');
        return;
      }
    } catch {
      phFailure({ step: 'signup_claim', message: 'network_error' });
      setLoading(false);
      setError('We could not reach the server. Check your connection and try again.');
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email: normalizedEmail,
      options: {
        emailRedirectTo: verifyUrl,
        // The claim call above created the user if it was ever going to
        // exist — with public signups off this must never try to create one.
        shouldCreateUser: false,
      },
    });

    setLoading(false);

    if (error) {
      // 2026-08-08: OTP send failures were silent in analytics — the signup
      // funnel showed a drop with no way to tell "changed their mind" apart
      // from "our email provider rejected them".
      phFailure({
        step: 'signup_otp_send',
        message: error.message,
        code: (error as { status?: number }).status,
      });
      setError(error.message);
      return;
    }

    markSignInStarted('email');
    phSignupOtpSent(normalizedEmail);
    // `intent=signup` lets /verify tell a genuine registration apart from a
    // returning login — see the CompleteRegistration gate in Verify.tsx.
    navigate(
      `/verify?email=${encodeURIComponent(normalizedEmail)}&intent=signup`,
    );
  };

  /* Google signup is back (2026-10-09) now that accounts are free. It was
     taken off on 2026-08-26 because OAuth can't be held back until
     payment. For a brand new email it only works when "Allow new users to
     sign up" is on in Supabase Auth. If it's off, Supabase bounces them
     back and AppGate returns them here with a message to use email. */
  const signupWithGoogle = async () => {
    setError(null);
    phOauthStarted({ provider: 'google', intent: 'signup' });
    markSignInStarted('google');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard?from=signup` },
    });
    if (error) {
      phFailure({ step: 'signup_oauth_google', message: error.message });
      setError(error.message);
    }
  };

  /* ===============================
     UI
  =============================== */
  if (finishingPayment) {
    return (
      <div className="auth-container">
        <header className="auth-header">
          <a
            href="https://mediloop.ai"
            className="logo"
            aria-label="Go to Mediloop homepage"
          >
            <img
              src="/images/logos/tail-logo.png"
              alt="Mediloop"
              className="logo-img"
              width={36}
              height={36}
            />
          </a>
        </header>

        <main className="auth-card is-welcome" aria-busy="true">
          <h1>
            Payment <span className="italic">received.</span>
          </h1>
          <p className="subtitle">Opening your case, this takes a few seconds.</p>
          <PlanSummary plan={checkoutPlan} />
        </main>
      </div>
    );
  }

  /* Free signup, the screen most people see. Same card as before, with
     Google back on top and nothing but the privacy line under the button.
     Paid and returning visitors keep the card below. */
  if (!checkoutSuccess && !isReturning) {
    // On its way to /start (see the effect above), so don't flash this form.
    if (!searchParams.get('error')) return null;
    return (
      <div className="auth-container">
        <header className="auth-header">
          <a href="https://mediloop.ai" className="logo" aria-label="Go to the mediloop homepage">
            <img src="/images/logos/tail-logo.png" alt="mediloop" className="logo-img" width={36} height={36} />
          </a>
        </header>

        <main className="auth-card su-card">
          <h1>
            Check your bill <span className="italic">before you pay it.</span>
          </h1>

          <button type="button" className="su-google has-last-used" onClick={signupWithGoogle}>
            {lastUsed === 'google' && <LastUsedTag />}
            <img src={googleLogo} alt="" />
            Sign up with Google
          </button>

          <div className="su-or">
            <span />
            <p>or</p>
            <span />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleEmailSignup();
            }}
          >
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              maxLength={40}
            />
            <div className="has-last-used">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              {lastUsed === 'email' && <LastUsedTag />}
            </div>

            {error && <p className="error">{error}</p>}

            <button type="submit" className="primary" disabled={!email || !firstName.trim() || loading}>
              {loading ? 'Sending code…' : 'Continue'}
            </button>
          </form>

          <div className="su-privacy">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            <p>Your bill stays private. HIPAA compliant and encrypted.</p>
          </div>

          <AuthHelp page="signup" email={email} label="Stuck?" />

          <p className="login-link">
            Already have an account?{' '}
            <Link to="/login" className="login-link-action">
              Log in
            </Link>
          </p>

          <p className="terms">
            By continuing, you agree to our{' '}
            <a href="https://mediloop.ai/terms-of-service" target="_blank" rel="noreferrer">
              Terms
            </a>{' '}
            and{' '}
            <a href="https://mediloop.ai/privacy-policy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <header className="auth-header">
        <a
          href="https://mediloop.ai"
          className="logo"
          aria-label="Go to Mediloop homepage"
        >
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </a>
      </header>

      <main className={`auth-card ${checkoutSuccess ? 'is-welcome' : ''}`}>
        {checkoutSuccess ? (
          <>
            <AuthStepper currentStep="signup" />
            <h1>
              Welcome to <span className="italic">mediloop.</span>
            </h1>
            <p className="subtitle">
              Your case file is open. Agent Loop is ready to start the
              investigation, we just need 60 seconds to get you set up.
            </p>
            <PlanSummary plan={checkoutPlan} />
          </>
        ) : (
          <>
            {isReturning && (
              <div className="success-banner" role="status" aria-live="polite">
                <span className="success-text">
                  We found your membership,{' '}
                  <Link to="/login" className="success-link">
                    let's finish setting up your account
                  </Link>
                </span>
              </div>
            )}
            {isReturning ? (
              <>
                <h1>Let's create <span className="italic">your account.</span></h1>
                <p className="subtitle">
                  Let Agent Loop get to work on your medical bills.
                </p>
              </>
            ) : (
              <>
                <h1>Check your bill <span className="italic">before you pay it.</span></h1>
                <p className="subtitle">
                  Create a free account, upload your bill and see what our
                  first look finds. It's $129 flat if you want a specialist to
                  take it on, refunded if we don't save you at least that.
                </p>
              </>
            )}
          </>
        )}

        <input
          type="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />

        {error && <p className="error">{error}</p>}

        {!checkoutSuccess && !isReturning && (
          <p className="terms" style={{ marginTop: 0 }}>
            No password. We email you a code to sign in.
          </p>
        )}

        <button
          className="primary"
          onClick={handleEmailSignup}
          disabled={!email || loading}
        >
          {loading
            ? 'Sending code…'
            : checkoutSuccess || isReturning
            ? 'Create account'
            : 'Email me a code'}
        </button>

        <AuthHelp page="signup" email={email} label="Stuck?" />

        <p className="login-link">
          {isReturning ? (
            <>
              Used a different email?{' '}
              <Link to="/login" className="login-link-action">
                Try another
              </Link>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <Link to="/login" className="login-link-action">
                Log in
              </Link>
            </>
          )}
        </p>

        <p className="terms">
          By continuing, you agree to our{' '}
          <a
            href="https://mediloop.ai/terms-of-service"
            target="_blank"
            rel="noreferrer"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href="https://mediloop.ai/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>
      </main>
    </div>
  );
}
